import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import WarningAmber from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import { SxProps } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {
  OutboundEmailRecipientState,
  OutboundEmailState,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';

import { OutboundEmail } from '~hooks/useOutboundEmails/models';
import isEnumValue from '~utils/isEnumValue';

type DeliveryStatus = 'pending' | 'delivered' | 'failed';

const deliveryStatusByEmailState = {
  [OutboundEmailState.RENDERING]: 'pending',
  [OutboundEmailState.SENDING]: 'pending',
  [OutboundEmailState.SENT]: 'delivered',
  [OutboundEmailState.FAILED]: 'failed',
} satisfies Record<OutboundEmailState, DeliveryStatus>;

const deliveryStatusByRecipientState = {
  [OutboundEmailRecipientState.PENDING]: 'pending',
  [OutboundEmailRecipientState.SENT]: 'delivered',
  [OutboundEmailRecipientState.FAILED]: 'failed',
} satisfies Record<OutboundEmailRecipientState, DeliveryStatus>;

const getDeliveryStatusInfo = (status: DeliveryStatus) => {
  switch (status) {
    case 'pending':
      return {
        label: t(
          'approvals.driver_pay.settlements.settlements_email_deliveries.states.initiated',
        ),
        icon: (
          <CheckCircleOutline sx={{ color: 'text.secondary', mr: 1 }} fontSize="small" />
        ),
      };
    case 'delivered':
      return {
        label: t(
          'approvals.driver_pay.settlements.settlements_email_deliveries.states.delivered',
        ),
        icon: <CheckCircleOutline color="success" sx={{ mr: 1 }} fontSize="small" />,
      };
    case 'failed':
      return {
        label: t(
          'approvals.driver_pay.settlements.settlements_email_deliveries.states.failed',
        ),
        icon: <WarningAmber color="warning" sx={{ mr: 1 }} fontSize="small" />,
      };
  }
};

// A state newer than this build's spec renders as pending instead of crashing.
const getOutboundEmailStatusInfo = (state: OutboundEmailState) =>
  getDeliveryStatusInfo(
    isEnumValue(OutboundEmailState, state)
      ? deliveryStatusByEmailState[state]
      : 'pending',
  );

export const getOutboundEmailRecipientStatusInfo = (state: OutboundEmailRecipientState) =>
  getDeliveryStatusInfo(
    isEnumValue(OutboundEmailRecipientState, state)
      ? deliveryStatusByRecipientState[state]
      : 'pending',
  );

interface SettlementOutboundEmailStatusProps {
  outboundEmail: OutboundEmail;
  sx?: SxProps;
  labelSx?: SxProps;
}

const SettlementOutboundEmailStatus = ({
  outboundEmail,
  sx,
  labelSx,
}: SettlementOutboundEmailStatusProps) => {
  const { icon, label } = getOutboundEmailStatusInfo(outboundEmail.state);

  return (
    <Box
      sx={[
        {
          display: 'flex',
          alignItems: 'center',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {icon}
      <Typography variant="body2" sx={labelSx}>
        {label}
        {' • '}
        {dayjs.tz(outboundEmail.createdAt).format('MMM DD')}
      </Typography>
    </Box>
  );
};

export default SettlementOutboundEmailStatus;
